<template>
  <div>
    <br>
    <h4 class="text-center">
      {{ $t('Sign in by entering the information below') }}
    </h4>
    <br>
    <p class="text-center register-now">
      <router-link :to="{name: 'registration'}">
        {{ $t('Register now') }}
      </router-link>
    </p>
    <Form
      ref="authForm"
      :model="authData"
      :rules="authRules"
      @submit.native.prevent
      @keyup.enter.native.prevent="onEnter"
    >
      <FormItem prop="email">
        <i-input
          v-model="authData.email"
          autofocus="autofocus"
          :placeholder="$t('Email address')"
          name="email"
          autocomplete="on"
          @on-change="onEmailChange"
        />
      </FormItem>
      <!-- Password only appears once we know this email is NOT an SSO account -->
      <FormItem
        v-if="step === 'password'"
        prop="password"
      >
        <i-input
          v-model="authData.password"
          autofocus="autofocus"
          :placeholder="$t('Password')"
          :type="showPassword ? 'text':'password'"
        >
          <Icon
            slot="suffix"
            :type="showPassword ? 'ios-eye-off':'ios-eye'"
            size="25"
            class="pointer-cursor"
            @click="showPassword = !showPassword"
          />
        </i-input>
      </FormItem>
    </Form>

    <!-- Step 1: continue (resolves whether the email uses SSO) -->
    <Button
      v-if="step === 'email'"
      long
      class="confirm-button"
      :loading="resolving"
      @click="continueWithEmail"
    >
      {{ $t('Continue') }}
    </Button>

    <!-- Step 2a: SSO account -> Microsoft button -->
    <Button
      v-if="step === 'sso'"
      long
      class="sso-button"
      icon="logo-windows"
      @click="signInWithMicrosoft"
    >
      {{ $t('Sign in with Microsoft') }}
    </Button>

    <!-- Step 2b: password account -> classic sign in -->
    <Button
      v-if="step === 'password'"
      long
      class="confirm-button"
      :loading="signInLoading"
      @click="triggerSignIn"
    >
      {{ $t('Sign in') }}
    </Button>

    <!-- Let the user change the email once past step 1 -->
    <p
      v-if="step !== 'email'"
      class="text-center change-email"
    >
      <a @click="resetToEmail">{{ $t('Use a different email') }}</a>
    </p>
    <p class="text-center lost-password">
      <router-link :to="{name: 'lost-password'}">
        {{ $t('Lost password') }}
      </router-link>
    </p>
  </div>
</template>

<script>
  import {postLoginQry, getAccountQry, postSsoResolveQry} from "../../services/auth.service"
  import {setAuthSession} from "../../helpers/auth.helper"

  export default {
    name: "Login",
    components:{
    },
    data() {
      return {
        // 'email'   -> asking for the email (step 1)
        // 'sso'     -> this email uses SSO, show the Microsoft button
        // 'password'-> classic email/password login
        step: "email",
        resolving: false,
        ssoAuthorizeUrl: "",
        showPassword: false,
        signInLoading: false,
        authData: {
          email: "",
          password: ""
        },
        authRules: {
          email: [
            {required: true, message: this.$t('Please provide an email'), trigger: 'blur'},
          ],
          password: [
            {required: true, message: this.$t('Please provide a password'), trigger: 'blur'},
          ]
        },
      }
    },
    methods: {
      onEnter() {
        if (this.step === "email") this.continueWithEmail();
        else if (this.step === "password") this.triggerSignIn();
      },
      onEmailChange() {
        // Editing the email invalidates a previous resolution.
        if (this.step !== "email") this.resetToEmail();
      },
      resetToEmail() {
        this.step = "email";
        this.ssoAuthorizeUrl = "";
        this.authData.password = "";
      },
      // Step 1: ask the backend whether this email's domain uses SSO.
      continueWithEmail() {
        if (!this.authData.email) {
          this.$Message.warning(this.$t('Please provide an email'));
          return;
        }
        this.resolving = true;
        postSsoResolveQry(this.authData.email)
          .then((res) => {
            if (res.data && res.data.sso) {
              this.ssoAuthorizeUrl = res.data.authorize_url;
              this.step = "sso";
            } else {
              this.step = "password";
            }
          })
          .catch(() => {
            // If discovery fails, fall back to classic password login.
            this.step = "password";
          })
          .finally(() => {
            this.resolving = false;
          });
      },
      signInWithMicrosoft() {
        // Full-page redirect to the backend, which starts the OIDC dance for the
        // resolved client (?sso=<id>) and returns to /sso-callback with a token.
        const path = this.ssoAuthorizeUrl || "/auth/oidc/authenticate/";
        window.location.href = process.env.VUE_APP_API_URL + path;
      },
      triggerSignIn() {
        this.$refs['authForm'].validate((valid) => {
          if (valid) {
            this.signInLoading = true;
            postLoginQry(this.authData)
              .then(res => {
                getAccountQry({}, res.data.access_token)
                  .then(res2 => {
                    let sessionToSave = Object.assign(res.data, res2.data);
                    setAuthSession(sessionToSave);
                    this.$router.push({
                      name: "map",
                    });
                    this.signInLoading = false;
                  })
                  .catch(() => {
                    this.$Message.error({content: this.$t('Cannot retrieve user detail'), duration: 5})
                  })
              })
              .catch((err) => {
                let message = this.$t('A server error occurred during authentication');
                if (err.response && err.response.status == 400) { // err.request is also available
                    message = this.$t('Invalid username/password');
                }
                this.$Message.error({content: message, duration: 5});
                this.signInLoading = false;
              })
          }
        })
      },
    }
  }
</script>

<style scoped>

    .lost-password {
        margin-top: 5px;
        font-size: 11px;
        text-decoration: underline;
    }

    .register-now {
        margin-bottom: 10px;
        text-decoration: underline;
    }

    .change-email {
        margin-top: 10px;
        font-size: 12px;
    }
    .change-email a {
        text-decoration: underline;
        cursor: pointer;
    }

</style>
